import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import MKBox from 'components/MaterialKit/MKBox';
import { formatGroupName, handleErrorResponse } from 'utils/general';
import { withLoading } from 'utils/hoc';
import { useAuth } from 'contexts/auth';
import MKTypography from 'components/MaterialKit/MKTypography';
import { getWhatsappMessage } from 'api/whatsapps';
import moment from 'moment';
import { DATE_FORMAT_DATE_TIME_DEFAULT } from 'utils/datetime';
import Image from 'components/Image';
import { getIWatchAccounts } from 'api/iwatch';
import { parsePhoneNumberFromString, ParseError } from 'libphonenumber-js/max';

const RawMessageDetailSection = ({
  setLoading,
}) => {
  const [message, setMessage] = useState(null);
  const [searchParams] = useSearchParams();
  const { setAuth } = useAuth();
  const messageId = searchParams.get('id');
  const [iWatchAccountMap, setIWatchAccountMap] = useState({});

  const fetchMessageFromApi = useCallback(() => {
    if (!messageId) {
      return Promise.resolve();
    }
    setLoading(true);
    return getWhatsappMessage(messageId, { $expand: 'whatsapp_message_images' })
      .then(({ data }) => {
        setMessage(data);
      })
      .catch((error) => {
        handleErrorResponse(error, setAuth);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [messageId, setAuth, setLoading]);

  useEffect(() => {
    fetchMessageFromApi();
  }, [fetchMessageFromApi]);

  const fetchIWatchAccountsFromApi = useCallback(() => {
    getIWatchAccounts({ $orderby: 'sequence' }).then(({ data }) => {
      const map = {};
      data.forEach(iWatchAccount => {
        const accountName = iWatchAccount.name;
        const phone = iWatchAccount.phone;
        try {
          const phoneNumber = parsePhoneNumberFromString('+' + phone);
          const formattedPhoneNumber = phoneNumber && phoneNumber.formatInternational() || phone;
          map[accountName] = `${iWatchAccount.key} (${accountName}) (${formattedPhoneNumber})`;
        } catch (e) {
          if (e instanceof ParseError) {
            // Not a phone number, non-existent country, etc.
            console.log(e.message);
          } else {
            throw e;
          }
        }
      });
      setIWatchAccountMap(map);
    })
  }, [setAuth]);

  useEffect(() => {
    fetchIWatchAccountsFromApi();
  }, [fetchIWatchAccountsFromApi]);

  let messageTimestamp = null;
  if (message) {
    messageTimestamp = message.datetime;
    if (messageTimestamp == null) {
      messageTimestamp = message.createddate;
    }
  }
  const messageTimestampStr = messageTimestamp == null ? '' : moment(messageTimestamp).format(DATE_FORMAT_DATE_TIME_DEFAULT);

  return (
    <MKBox padding="1rem" width="100%">
      <MKTypography color="black" textAlign="center" fontSize="25px">Raw Message Detail</MKTypography>

      <MKTypography color="black" mt="1rem">Source Feeder Account (Phone Number):</MKTypography>
      <MKTypography color="blue">{iWatchAccountMap[message?.account]}</MKTypography>

      <MKTypography color="black" mt="1rem">Group Name:</MKTypography>
      <MKTypography color="blue">{message ? formatGroupName(message.group_name) : ''}</MKTypography>

      <MKTypography color="black" mt="1rem">Phone (Whatsapp):</MKTypography>
      <MKTypography color="blue">{message?.phone}</MKTypography>

      <MKTypography color="black" mt="1rem">Date:</MKTypography>
      <MKTypography color="blue">{messageTimestampStr}</MKTypography>

      <MKTypography color="black" mt="1rem">Message:</MKTypography>
      <MKTypography
        color="blue"
        style={{
          whiteSpace: 'pre-wrap',
        }}
      >
        {message?.message}
      </MKTypography>

      <MKBox mt="1rem">
        <MKTypography color="black">Images:</MKTypography>
        {(message?.whatsapp_message_images || []).map((image) => (<Image src={image.image_url} width="100%" style={{ maxWidth: '400px', marginRight: '20px' }} />))}
        {message?.image_url && <Image src={message?.image_url} width="100%" style={{ maxWidth: '400px', marginRight: '20px' }} />}
      </MKBox>
    </MKBox>
  );
};

RawMessageDetailSection.propTypes = {
  setLoading: PropTypes.func,
};

export default withLoading(RawMessageDetailSection);
